import type { OverlayRef } from '@angular/cdk/overlay';
import { Overlay } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import type { OnDestroy, OnInit, TemplateRef } from '@angular/core';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  inject,
  ViewChild,
  ViewContainerRef,
  ViewEncapsulation,
} from '@angular/core';
import type { MatButton } from '@angular/material/button';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { NotificationsService } from 'app/layout/common/notifications/notifications.service';
import type { Notification } from 'app/layout/common/notifications/notifications.types';
import { Subject, takeUntil } from 'rxjs';

@Component({
  selector: 'spri-notifications',
  templateUrl: './notifications.component.html',
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  exportAs: 'notifications',
  imports: [MatButtonModule, MatIconModule, MatTooltipModule],
})
export class NotificationsComponent implements OnInit, OnDestroy {
  @ViewChild('notificationsOrigin') private notificationsOrigin: MatButton;
  @ViewChild('notificationsPanel')
  private notificationsPanel: TemplateRef<unknown>;

  notifications: Notification[];
  unreadCount = 0;
  private overlayRef: OverlayRef;
  private unsubscribeAll: Subject<void> = new Subject<void>();

  /**
   * Constructor
   */

  // Use inject() for dependencies
  private readonly changeDetectorRef = inject(ChangeDetectorRef);
  private readonly notificationsService = inject(NotificationsService);
  private readonly overlay = inject(Overlay);
  private readonly viewContainerRef = inject(ViewContainerRef);

  /**
   * On init
   */
  ngOnInit(): void {
    // Subscribe to notification changes
    this.notificationsService.notifications$
      .pipe(takeUntil(this.unsubscribeAll))
      .subscribe((notifications: Notification[]) => {
        // Load the notifications
        this.notifications = notifications;

        // Calculate the unread count
        this.calculateUnreadCount();

        // Mark for check
        this.changeDetectorRef.markForCheck();
      });
  }

  /**
   * On destroy
   */
  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions
    this.unsubscribeAll.next(null);
    this.unsubscribeAll.complete();

    // Dispose the overlay
    if (this.overlayRef) {
      this.overlayRef.dispose();
    }
  }

  /**
   * Open the notifications panel
   */
  public openPanel(): void {
    // Return if the notifications panel or its origin is not defined
    if (!this.notificationsPanel || !this.notificationsOrigin) {
      return;
    }

    // Create the overlay if it doesn't exist
    if (!this.overlayRef) {
      this.createOverlay();
    }

    // Attach the portal to the overlay
    this.overlayRef.attach(new TemplatePortal(this.notificationsPanel, this.viewContainerRef));
  }

  /**
   * Close the notifications panel
   */
  public closePanel(): void {
    this.overlayRef.detach();
  }

  /**
   * Mark all notifications as read
   */
  public markAllAsRead(): void {
    // Mark all as read
    this.notificationsService.markAllAsRead().subscribe();
  }

  /**
   * Toggle read status of the given notification
   */
  public toggleRead(notification: Notification): void {
    // Toggle the read status
    notification.read = !notification.read;

    // Update the notification
    this.notificationsService.update(notification.id, notification).subscribe();
  }

  /**
   * Delete the given notification
   */
  public delete(notification: Notification): void {
    // Delete the notification
    this.notificationsService.delete(notification.id).subscribe();
  }

  /**
   * Track by function for ngFor loops
   *
   * @param index
   * @param item
   */
  public trackByFn(index: number, item: Notification): string | number {
    return item.id || index;
  }

  /**
   * Create the overlay
   */
  private createOverlay(): void {
    // Create the overlay
    this.overlayRef = this.overlay.create({
      hasBackdrop: true,
      backdropClass: 'spri-backdrop-on-mobile',
      scrollStrategy: this.overlay.scrollStrategies.block(),
      positionStrategy: this.overlay
        .position()
        .flexibleConnectedTo(this.notificationsOrigin._elementRef.nativeElement)
        .withLockedPosition(true)
        .withPush(true)
        .withPositions([
          {
            originX: 'start',
            originY: 'bottom',
            overlayX: 'start',
            overlayY: 'top',
          },
          {
            originX: 'start',
            originY: 'top',
            overlayX: 'start',
            overlayY: 'bottom',
          },
          {
            originX: 'end',
            originY: 'bottom',
            overlayX: 'end',
            overlayY: 'top',
          },
          {
            originX: 'end',
            originY: 'top',
            overlayX: 'end',
            overlayY: 'bottom',
          },
        ]),
    });

    // Detach the overlay from the portal on backdrop click
    this.overlayRef.backdropClick().subscribe(() => {
      this.overlayRef.detach();
    });
  }

  /**
   * Calculate the unread count
   *
   * @private
   */
  private calculateUnreadCount(): void {
    let count = 0;

    if (this.notifications && this.notifications.length) {
      count = this.notifications.filter((notification) => !notification.read).length;
    }

    this.unreadCount = count;
  }
}
